import { Container, Typography, Box, Grid, Button, Stack, Alert } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Surface } from '../components/primitives';

const features = [
    { feature: 'Historical Congress stock trades',         free: true,  premium: true, developer: true },
    { feature: 'Congressional trader leaderboard',         free: true,  premium: true, developer: true },
    { feature: 'Issuer pages & per-ticker activity',       free: true,  premium: true, developer: true },
    { feature: 'Congressional trading analytics',          free: true,  premium: true, developer: true },
    { feature: 'Backtested Track Record',                  free: true,  premium: true, developer: true },
    { feature: 'Congressional options trading',            free: false, premium: true, developer: true },
    { feature: 'Top stock picks & CG Score rankings',      free: false, premium: true, developer: true },
    { feature: 'Member watchlists',                        free: false, premium: true, developer: true },
    { feature: 'Email alerts on watched members',          free: false, premium: true, developer: true },
    { feature: 'Weekly digest email',                      free: false, premium: true, developer: true },
    { feature: 'Full data exports (CSV, Excel, JSON)',     free: false, premium: false, developer: true },
    { feature: 'API access',                               free: false, premium: false, developer: true },
    { feature: 'Dedicated support',                        free: false, premium: false, developer: true },
];

const tiers = [
    {
        key: 'free',
        title: 'Free',
        price: '$0',
        sub: 'forever',
        cta: 'Get started',
        ctaTo: '/sign-up',
        description: 'Browse all trades, issuers, and analytics. Sign in to save your leaderboard view.',
    },
    {
        key: 'premium',
        title: 'Pro',
        price: '$8',
        sub: 'per month',
        cta: 'Upgrade',
        ctaTo: '/account',
        featured: true,
        description: 'Options activity, our scored stock picks, and email alerts on members you watch.',
    },
    {
        key: 'developer',
        title: 'Developer+',
        price: 'Custom',
        sub: 'contact sales',
        cta: 'Coming soon',
        disabled: true,
        description: 'Raw exports, full API access, and dedicated support for teams and research.',
    },
];

const FeatureValue = ({ value }) => {
    if (value === true) {
        return (
            <Box sx={{
                width: 18, height: 18, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'custom.primary.400',
                color: '#fff',
            }}>
                <CheckIcon sx={{ fontSize: 12 }} />
            </Box>
        );
    }
    if (value === false) {
        return (
            <CloseIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        );
    }
    return (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {value}
        </Typography>
    );
};

const Pricing = () => {
    const { state } = useLocation();
    return (
    <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
        {state?.proRequired && (
            <Alert severity="info" sx={{ mb: 3 }}>
                That page is part of Capitol Gains Pro. Pick a plan below to unlock it.
            </Alert>
        )}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h1" sx={{ color: 'text.primary', mb: 1 }}>
                Simple, transparent pricing
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto' }}>
                Trades, leaderboards, and analytics are free for everyone. Upgrade to Pro
                for options data, our scored stock picks, and real-time member alerts.
            </Typography>
        </Box>

        {/* Plan cards */}
        <Grid container spacing={2} sx={{ mb: 6 }}>
            {tiers.map(tier => (
                <Grid item xs={12} md={4} key={tier.key}>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3 }}
                    >
                        <Surface
                            variant={tier.featured ? 'accent' : 'default'}
                            padding={3.5}
                            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                        >
                            <Stack spacing={1} sx={{ mb: 3 }}>
                                <Typography variant="overline" sx={{ color: tier.featured ? 'primary.light' : 'text.secondary' }}>
                                    {tier.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                    <Typography variant="h1" sx={{ color: 'text.primary', fontSize: '2.25rem' }}>
                                        {tier.price}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.muted' }}>
                                        {tier.sub}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                    {tier.description}
                                </Typography>
                            </Stack>
                            <Box sx={{ mt: 'auto' }}>
                                <Button
                                    variant={tier.featured ? 'contained' : 'outlined'}
                                    fullWidth
                                    disabled={tier.disabled}
                                    component={tier.disabled ? 'button' : Link}
                                    to={tier.disabled ? undefined : tier.ctaTo}
                                >
                                    {tier.cta}
                                </Button>
                            </Box>
                        </Surface>
                    </motion.div>
                </Grid>
            ))}
        </Grid>

        {/* Feature comparison */}
        <Box sx={{ mb: 3 }}>
            <Typography variant="h3" sx={{ color: 'text.primary', mb: 1 }}>
                Compare features
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                What you get with each plan.
            </Typography>
        </Box>

        <Surface padding={0} sx={{ overflow: 'hidden' }}>
            {/* Header row */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1.4fr 0.6fr 0.6fr 0.6fr', md: '2fr 1fr 1fr 1fr' },
                alignItems: 'center',
                px: 3, py: 2,
                borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                bgcolor: 'custom.surface.subtle',
            }}>
                <Typography variant="overline" sx={{ color: 'text.muted' }}>
                    Feature
                </Typography>
                {tiers.map(tier => (
                    <Typography
                        key={tier.key}
                        variant="overline"
                        sx={{ color: tier.featured ? 'primary.light' : 'text.secondary', textAlign: 'center' }}
                    >
                        {tier.title}
                    </Typography>
                ))}
            </Box>

            {features.map((row, i) => (
                <Box
                    key={i}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1.4fr 0.6fr 0.6fr 0.6fr', md: '2fr 1fr 1fr 1fr' },
                        alignItems: 'center',
                        px: 3, py: 1.75,
                        borderBottom: t => i < features.length - 1 ? `1px solid ${t.palette.custom.border.subtle}` : 'none',
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {row.feature}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}><FeatureValue value={row.free} /></Box>
                    <Box sx={{ textAlign: 'center' }}><FeatureValue value={row.premium} /></Box>
                    <Box sx={{ textAlign: 'center' }}><FeatureValue value={row.developer} /></Box>
                </Box>
            ))}
        </Surface>
    </Container>
    );
};

export default Pricing;
